import React, { useMemo } from 'react';
import lodash from 'lodash';
import classnames from 'classnames';
import Select from 'react-select';
import Input from 'components/common/Input/FormInput';
import InputField from 'components/common/Study/InputField';
import DndSection from '../DndSection';
import EventDetailWrapper from '../EventDetailWrapper';
import MultiCardSelect from 'components/common/MultiCardSelect/MultiCardSelect';
import {
    customStyles,
} from 'components/common/Dropdown/DropDownListHelper';
import { Button, BUTTON } from 'components/common/Button/Button';
const { SIZE, COLOR, BGCOLOR } = BUTTON;
import styles from './CreateEventType.scss';

export function PageBody(props) {
    return (
        <section className={classnames(styles.body, props.className)} data-cy="components-PageBody">
            {props.children}
        </section>
    );
}

export function PageSection({ children, className, topBorder = true }) {
    return (
        <section
            data-cy="components-PageSection"
            className={
                classnames(
                    styles.pageSection,
                    className, {
                        [styles.topBorder]: topBorder
                    }
                )
            }
        >
            {children}
        </section>
    );
}

export function SectionTitle(props) {
    return (
        <h2 className={classnames(styles.pageSectionTitle, props.className)} data-cy="components-SectionTitle">
            {props.children}
        </h2>
    );
}

export function SectionDescription(props) {
    return (
        <p className={classnames(styles.pageSectionDescription, props.className)} data-cy="components-SectionDescription">
            {props.children}
        </p>
    );
}

export function EventDefinition({
    title,
    isTitleValid,
    onTitleChange,
    description,
    onDescriptionChange,
}) {
    return (
        <PageSection topBorder={false} className={styles.titleAndDescription} data-cy="components-PageSection">
            <Input
                title={'Event Title'}
                placeholder={'Add a title for your event'}
                type={'text'}
                value={title}
                isValid={isTitleValid}
                errorText={'Title is required and must be unique'}
                required={true}
                onBlur={() => { }}
                onChange={(e) => onTitleChange(e.target.value.trim())}
                data-cy="components-onTitleChange"
                maxLength={140}
            />

            <Input
                title={'Event Description'}
                placeholder={'Add a description for your event'}
                type={'text'}
                value={description}
                isValid={() => true}
                required={false}
                onBlur={() => { }}
                onChange={(e) => onDescriptionChange(e.target.value.trim())}
                data-cy="components-onDescriptionChange"
            />
        </PageSection>
    );
}

export function EventDetails({
    dataSources,
    details,
    onItemOrderChanged,
    onItemChanged,
    onRemoveItem,
    title,
    onAddEventDetail
}) {
    return (
        <PageSection data-cy="components-PageSection">
            <SectionTitle>Event Detail Form Options</SectionTitle>
            <SectionDescription>
                Add event details using the button below. Drag-and-drop your event details to control the order these items appear when the customer is creating a new event of this type. Expand each item to edit the details.
            </SectionDescription>

            <DndSection
                key={'create-event-type-dnd'}
                view={EventDetailWrapper}
                dataSources={dataSources}
                items={details}
                onItemOrderChanged={onItemOrderChanged}
                onRemoveItem={onRemoveItem}
                title={(title.value)}
                onItemChanged={onItemChanged}
            />

            <Button
                text={'Add Event Detail'}
                type={SIZE.PRIMARY}
                color={COLOR.WHITE}
                bgcolor={BGCOLOR.BLUE}
                onClick={onAddEventDetail}
                disabled={false}
                data-cy="components-Button"
            />
        </PageSection>
    );
}

export function generateDropdownValues(rawValues, labelAttr, valueAttr) {
    const dropdownValues = [];
    rawValues.map(value => {
        dropdownValues.push({
            label: value[labelAttr],
            value: value[valueAttr],
        });
    });
    return dropdownValues;
}


export function PrimaryDateSettings({
    dateDetailOptions = [],
    setPrimaryDateDetail,
    primaryDateDetail,
    onDetailChanged,
}) {
    const dropdownValues = generateDropdownValues(dateDetailOptions, 'title', 'id');
    return (
        <PageSection  data-cy="components-generateDropdownValues">
            <SectionTitle>
                Primary Date
            </SectionTitle>
            <SectionDescription>
                Select one date-type event detail to serve as the Primary Date for chronological ordering
            </SectionDescription>
            <InputField
                required
                label="Primary Date"
                htmlFor="primarydate"
                data-cy="components-InputField"
            >
                <Select
                    name='primarydate'
                    isDisabled={false}
                    isClearable={true}
                    options={dropdownValues}
                    value={primaryDateDetail}
                    styles={customStyles}
                    onChange={(value) => onDetailChanged(setPrimaryDateDetail, value)}
                    data-cy="components-Select"
                />
            </InputField>

        </PageSection>
    );
}

export function DisplaySettings({
    displaySettingsOptions = [],
    firstDisplayDetail,
    secondDisplayDetail,
    thirdDisplayDetail,
    setFirstDisplayDetail,
    setSecondDisplayDetail,
    setThirdDisplayDetail,
    onDetailChanged,
}) {
    const dropdownValues = generateDropdownValues(displaySettingsOptions, 'title', 'id');
    return (
        <PageSection data-cy="components-generateDropdownValues">
            <SectionTitle>
                Display Settings
            </SectionTitle>
            <SectionDescription>
                Select up to 3 options to display on the event list items and in the event export headers. The ordering of these dropdowns reflects the ordering of their display.
            </SectionDescription>
            <InputField
                required={false}
                label="Detail 1 — displayed in summary header in export"
                htmlFor="detailone"
                data-cy="components-InputFields"
            >
                <Select
                    name='detailone'
                    isClearable={true}
                    options={dropdownValues}
                    styles={customStyles}
                    value={firstDisplayDetail}
                    onChange={(value) => onDetailChanged(setFirstDisplayDetail, value)}
                    data-cy="components-Selects"
                />
            </InputField>
            <InputField
                required={false}
                label="Detail 2 — displayed in summary header in export"
                htmlFor="detailtwo"
                data-cy="components-InputFieldt"
            >
                <Select
                    name='detailtwo'
                    isClearable={true}
                    options={dropdownValues}
                    styles={customStyles}
                    value={secondDisplayDetail}
                    onChange={(value) => onDetailChanged(setSecondDisplayDetail, value)}
                    data-cy="components-Selectd"
                />
            </InputField>
            <InputField
                required={false}
                label="Detail 3"
                htmlFor="detailthree"
                data-cy="components-detailthreeInput"
            >
                <Select
                    name='detailthree'
                    isClearable={true}
                    options={dropdownValues}
                    styles={customStyles}
                    value={thirdDisplayDetail}
                    onChange={(value) => onDetailChanged(setThirdDisplayDetail, value)}
                    data-cy="components-detailthreeSelect"
                />
            </InputField>
        </PageSection>
    );
}

export function mapIntegrationToDropdownData(integrations) {
    return lodash.map(integrations, ({ mappingId, name }) => ({ id: mappingId, value: mappingId, label: name }));
}

export function IntegrationSettings({ integrationOptions, selectedIntegrations, setSelectedIntegration }) {
    const selectOptions = useMemo(() => mapIntegrationToDropdownData(integrationOptions), [integrationOptions]);

    const handleChange = (selected) => {
        setSelectedIntegration(state => [...state, ...selected]);
    };

    const handleRemove = (removed) => {
        setSelectedIntegration(state => {
            const remaining = lodash.filter(state, ({ value }) => value !== removed.value);
            setSelectedIntegration(remaining);
        });
    };

    return (
        <PageSection data-cy="components-IntegrationSettings">
            <SectionTitle>
                Integration Partners
            </SectionTitle>
            <SectionDescription>
                Select integration partners that this event is exported to
            </SectionDescription>
            <InputField
                required={false}
                htmlFor="integration-partners"
                data-cy="components-partnersInput"
            >
                <MultiCardSelect
                    name='integration-partners'
                    isMulti
                    isClearable={true}
                    isDisabled={lodash.isEmpty(selectOptions)}
                    options={selectOptions}
                    value={selectedIntegrations}
                    placeholder="Select Integration Partners"
                    styles={customStyles}
                    onChange={handleChange}
                    onRemove={handleRemove}
                    data-cy="components-MultiCardSelect"
                />
            </InputField>
        </PageSection>
    );
}

export function ActionButtons({ onCancelClicked, onCreateClicked, createDisabled }) {
    return (
        <PageSection className={styles.actionButtons}  data-cy="components-ActionButtons">
            <Button
                text={'Cancel New Event'}
                type={SIZE.PRIMARY}
                color={COLOR.RED}
                bgcolor={BGCOLOR.WHITE}
                onClick={onCancelClicked}
                data-cy="components-BtnNew"
            />

            <Button
                text={'Create Event'}
                type={SIZE.PRIMARY}
                color={COLOR.WHITE}
                bgcolor={BGCOLOR.ORANGE}
                onClick={onCreateClicked}
                disabled={createDisabled}
                data-cy="components-BtnCreate"
            />
        </PageSection>
    );
}
